import React, {Suspense, lazy, useEffect} from 'react';
import { Navigate, Routes, Route, useSearchParams, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CorpayTheme from './helpers/CorpayTheme';
import ProtectedRoute from './helpers/ProtectedRoute';
import './App.css';

// LANDING + COMMON PAGES
import Forbidden from './pages/Forbidden';
import Login from './pages/Login';
import MultifactorAuth from './components/MFA';
import NotFound from './pages/NotFound';
import PDFDisplay from './pages/common/PDFDisplay';
import { APIConnector } from './api/APIConnector';
import TokenLogin from './pages/TokenLogin';
import SsoLanding from './pages/SsoLanding';

// CHUNK FAILED AUTO RELOADER
function u2d(componentImport) {
	return new Promise((resolve, reject) => {
		componentImport().then((component) => {
			window.sessionStorage.setItem('retry-chunk', 'false');
			resolve(component);
		}).catch((error) => {
			const hasRefreshed = JSON.parse(window.sessionStorage.getItem('retry-chunk') || 'false');
			if (!hasRefreshed) { // not been refreshed yet
				window.sessionStorage.setItem('retry-chunk', 'true');
				return window.location.reload(); // refresh the page
			}
			reject(error);
		});
	});
}

// APPROVAL ROUTING IMPORTS
const ApprovalLogin = lazy(() => u2d(() => import('./pages/approval/ApprovalLogin')));
const ApprovalDashboard = lazy(() => u2d(() => import('./pages/approval/ApprovalDashboard')));

// SPANISH FIRM IMPORTS
const ForeignTaxRegistration = lazy (() => u2d(() => import('./pages/spanish/vendor/ForeignTaxRegistration')));
const InvoicesDue = lazy(() => u2d(() => import('./pages/spanish/vendor/InvoicesDue')));
const PayeeInvite = lazy(() => u2d(() => import('./pages/spanish/client/PayeeInvite')));
const SpanishSubmissionReport = lazy(() => u2d(() => import('./pages/spanish/client/SubmissionReport')));
const TaxBridgeLoginPage = lazy(() => u2d(() => import('./pages/spanish/TaxBridgeLoginPage')));
const UploadInvoices = lazy(() => u2d(() => import('./pages/spanish/client/UploadInvoices')));

// USPTO IMPORTS
const CodeManagement = lazy(() => u2d(() => import('./pages/uspto/CodeManagement')));
const ClientCodes = lazy(() => u2d(() => import('./pages/uspto/ClientCodes')));
const DailyHistory = lazy(() => u2d(() => import('./pages/uspto/daily_history')));
const DataReview = lazy(() => u2d(() => import('./pages/uspto/DataReview')));
const Demo = lazy(() => u2d(() => import('./pages/uspto/Demo')));
const DocketMain = lazy(() => u2d(() => import('./pages/uspto/DocketMain')));
const ImportData = lazy(() => u2d(() => import('./pages/uspto/ImportData')));
const MatterMain = lazy(() => u2d(() => import('./pages/uspto/MatterMain')));
const MissingUsers = lazy(() => u2d(() => import('./pages/uspto/MissingUsers')));
const ParkedCodes = lazy(() => u2d(() => import('./pages/uspto/ParkedCodes')));
const Refunds = lazy(() => u2d(() => import('./pages/uspto/Refunds')));
const SummaryReport = lazy(() => u2d(() => import('./pages/uspto/SummaryReport')));
const Reconciliation = lazy(() => u2d(() => import('./pages/uspto/Reconciliation')));
const Reporting = lazy(() => u2d(() => import('./pages/uspto/Reporting')));
const ValidMaintenance = lazy(() => u2d(() => import('./pages/uspto/ValidMaintenance')));
const UsptoChangePassword = lazy(() => u2d(() => import('./pages/uspto/ChangePassword')));
const UsptoClientSetup = lazy(() => u2d(() => import('./pages/uspto/ClientSetup')));
const UsptoClientBilling = lazy(() => u2d(() => import('./pages/uspto/ClientBilling')));
const UsptoExportFiles = lazy(() => u2d(() => import('./pages/uspto/ExportFiles')));
const TransactionSearch = lazy(() => u2d(() => import('./pages/uspto/TransactionSearch')));
const UsptoDashboard = lazy(() => u2d(() => import('./pages/uspto/UsptoDashboard')));
const UsptoExportFile = lazy(() => u2d(() => import('./pages/uspto/UsptoExportFile')));
const UsptoDataGridSetup = lazy(() => u2d(() => import('./pages/uspto/UsptoDataGridSetup')));
const UsptoMatterConflicts = lazy(() => u2d(() => import('./pages/uspto/UsptoMatterConflicts')));
const UsptoLogin = lazy(() => u2d(() => import('./pages/uspto/UsptoLogin')));
const UsptoResetPassword = lazy(() => u2d(() => import('./pages/uspto/UsptoResetPassword')));
const USPTOMatterLogin = lazy(() => u2d(() => import('./pages/uspto/USPTOMatterLogin')));
const UsptoMenuShell = lazy(() => u2d(() => import('./pages/uspto/UsptoMenuShell')));
const UsptoUserSetup = lazy(() => u2d(() => import('./pages/uspto/UserSetup')));
const TransactionReport = lazy(() => u2d(() => import('./pages/uspto/TransactionReport')));

// USCIS IMPORTS
const NavigatorForm = lazy(() => u2d(() => import('./pages/uscis/NavigatorForm')));
const G1450Review = lazy(() => u2d(() => import('./pages/uscis/G1450Review')));
const G1450ReviewAdmin = lazy(() => u2d(() => import('./pages/uscis/G1450ReviewAdmin')));
const CardReviewAdmin = lazy(() => u2d(() => import('./pages/uscis/CardReviewAdmin')));
const DataIssuesAdmin = lazy(() => u2d(() => import('./pages/uscis/DataIssuesAdmin')));
const UscisDemo = lazy(() => u2d(() => import('./pages/uscis/Demo')));
const UscisExtension = lazy(() => u2d(() => import('./pages/uscis/ExtensionDownload')));
const UscisChangePassword = lazy(() => u2d(() => import('./pages/uscis/ChangePassword')));
const UscisDailyHistory = lazy(() => u2d(() => import('./pages/uscis/DailyHistory')));
const UscisDashboard = lazy(() => u2d(() => import('./pages/uscis/UscisDashboard')));
const UscisImportData = lazy(() => u2d(() => import('./pages/uscis/ImportData')));
const UscisImportH1B = lazy(() => u2d(() => import('./pages/uscis/ImportH1B')));
const UscisH1BCapReview = lazy(() => u2d(() => import('./pages/uscis/H1BCapReview')));
const UscisLogin = lazy(() => u2d(() => import('./pages/uscis/UscisLogin')));
const UscisResetPassword = lazy(() => u2d(() => import('./pages/uscis/UscisResetPassword')));
const UscisG1450Login = lazy(() => u2d(() => import('./pages/uscis/UscisG1450Login')));
const UscisMenuShell = lazy(() => u2d(() => import('./pages/uscis/UscisMenuShell')));
const UscisTry = lazy(() => u2d(() => import('./pages/uscis/UscisTry')));
const UscisAbout = lazy(() => u2d(() => import('./pages/uscis/UscisAbout')));
const UscisSignup = lazy(() => u2d(() => import('./pages/uscis/UscisSignup')));
const UscisReconciliation = lazy(() => u2d(() => import('./pages/uscis/Reconciliation')));
const UscisUnProcessed = lazy(() => u2d(() => import('./pages/uscis/Unprocessed')));
const UscisUnProcessedAdmin = lazy(() => u2d(() => import('./pages/uscis/UnprocessedAdmin')));
const UscisReview = lazy(() => u2d(() => import('./pages/uscis/Review')));
const UscisCardDecline = lazy(() => u2d(() => import('./pages/uscis/CardDecline')));
const UscisCardEvent = lazy(() => u2d(() => import('./pages/uscis/CardEvent')));
const UscisCardRefund = lazy(() => u2d(() => import('./pages/uscis/CardRefund')));
const UscisExpiredCards = lazy(() => u2d(() => import('./pages/uscis/ExpiredCards')));
const UscisPartialPayments = lazy(() => u2d(() => import('./pages/uscis/PartialPayments')));
const UscisValidMaintenance = lazy(() => u2d(() => import('./pages/uscis/ValidMaintenance')));
const UscisClientSetup = lazy(() => u2d(() => import('./pages/uscis/ClientSetup')));
const UscisClientBilling = lazy(() => u2d(() => import('./pages/uscis/ClientBilling')));
const UscisClientFieldSetup = lazy(() => u2d(() => import('./pages/uscis/ClientFieldSetup')));
const UscisExtensionFieldSetup = lazy(() => u2d(() => import('./pages/uscis/ExtensionFieldSetup')));
const UscisCardControlSetup = lazy(() => u2d(() => import('./pages/uscis/CardControlSetup')));
const UscisExtensionSiteAccountSetup = lazy(() => u2d(() => import('./pages/uscis/ExtensionSiteAccountSetup')));
const UscisVendorMapping = lazy(() => u2d(() => import('./pages/uscis/VendorMapping')));
const UscisExportFiles = lazy(() => u2d(() => import('./pages/uscis/ExportFiles')));
const UscisUserSetup = lazy(() => u2d(() => import('./pages/uscis/UserSetup')));
const UscisClientImportSetup = lazy(() => u2d(() => import('./pages/uscis/ClientImportSetup')));
const UscisChromeRiverImport = lazy(() => u2d(() => import('./pages/uscis/ChromeRiverImport')));
const UscisCardVerification = lazy(() => u2d(() => import('./pages/uscis/CardVerification')));
const UscisSftpImports = lazy(() => u2d(() => import('./pages/uscis/SftpImports')));
const UscisClientDataGridSetup = lazy(() => u2d(() => import('./pages/uscis/ClientDataGridSetup')));
const UscisEventHistory = lazy(() => u2d(() => import('./pages/uscis/EventHistory')));
const UscisFormFeeCodes = lazy(() => u2d(() => import('./pages/uscis/FormFeeCodes')));
const UscisBalanceCalculation = lazy(() => u2d(() => import('./pages/uscis/BalanceCalculation')));

// USCIS clients with service 1151 'No G1450 Features Available' (e.g. browser-extension clients) do not
// get the G-1450 pages; dashboard_sr.list_menu hides the same items. (1040 'No 1450s' only stops G-1450
// PDFs from being built and never hides pages.) False until the user's services have loaded, so a page
// never renders before the check.
const usesG1450s = (context) => !!context.userInfo.loaded
	&& !(context.userInfo.services || []).includes('1151');

// The Navigator Form: G-1450 entry and approval, plus Card Requests for the users who approve extension
// card requests -- the only part a 1151 client gets (cardRequestApprover, from the database).
const usesNavigatorForm = (context) => usesG1450s(context)
	|| (!!context.userInfo.loaded && context.userInfo.cardRequestApprover === -1);

// The G-1450 form moved to /uscis/navigator-form; old links and bookmarks keep their query string.
const ToNavigatorForm = () => {
	const location = useLocation();
	return <Navigate to={`/uscis/navigator-form${location.search}`} replace />;
};

// Chrome River Import setup: clients with service 1176 'Chrome River Import' (the database also
// limits it to Corpay admins, as dashboard_sr.list_menu does for the menu item).
const usesChromeRiverImport = (context) => !!context.userInfo.loaded
	&& (context.userInfo.services || []).includes('1176');

// VENDOR IMPORTS
const Contact = lazy(() => u2d(() => import( './pages/vendor/Contact')));
const ClientTerms = lazy(() => u2d(() => import('./pages/vendor/ClientTerms')));
const ExceptionManagement = lazy(() => u2d(() => import('./pages/vendor/ExceptionManagement')));
const InvoiceReports = lazy(() => u2d(() => import('./pages/vendor/InvoiceReports')));
const InvoiceStatus = lazy(() => u2d(() => import('./pages/vendor/InvoiceStatus')));
const PaymentsReceived = lazy(() => u2d(() => import('./pages/vendor/PaymentsReceived')));
const PaymentsReceivedPost = lazy(() => u2d(() => import('./pages/vendor/PaymentsReceivedPost')));
const Registered = lazy(() => u2d(() => import('./pages/vendor/Registered')));
const Registration = lazy(() => u2d(() => import('./pages/vendor/Registration')));
const SubmissionReport = lazy(() => u2d(() => import('./pages/vendor/SubmissionReport')));
const SwitchAccount = lazy(() => u2d(() => import('./pages/vendor/SwitchAccount')));
const Tutorial = lazy(() => u2d(() => import('./pages/vendor/Tutorial')));
const VendorChangePassword = lazy(() => u2d(() => import('./pages/vendor/VendorChangePassword')));
const VendorDashboard = lazy(() => u2d(() => import('./pages/vendor/VendorDashboard')));
const VendorInvoiceEntry = lazy(() => u2d(() => import('./pages/vendor/VendorInvoiceEntry')));
const VendorMenuShell = lazy(() => u2d(() => import('./pages/vendor/VendorMenuShell')));
const VendorMessageCenter = lazy(() => u2d(() => import('./pages/vendor/VendorMessageCenter')));
const VendorPendingInvoicesWrapper = lazy(() => u2d(() => import('./pages/vendor/VendorPendingInvoicesWrapper')));
const VendorRejectedInvoicesWrapper = lazy(() => u2d(() => import('./pages/vendor/VendorRejectedInvoicesWrapper')));
const VendorUserAdmin = lazy(() => u2d(() => import('./pages/vendor/VendorUserAdmin')));

//CLIENT IMPORTS
const ActivityLogWrapper = lazy(() => u2d(() => import('./pages/client/ActivityLogWrapper')));
const AgingReports = lazy(() => u2d(() => import('./pages/client/AgingReports')));
const BatchUpload = lazy(() => u2d(() => import('./pages/client/BatchUpload')));
const BatchReview = lazy(() => u2d(() => import('./pages/client/BatchReview')));
const BatchInquiry = lazy(() => u2d(() => import('./pages/client/BatchInquiry')));
const BatchApproval = lazy(() => u2d(() => import('./pages/client/BatchApproval')));
const ClientChangePassword = lazy(() => u2d(() => import('./pages/client/ClientChangePassword')));
const ClientDashboard = lazy(() => u2d(() => import('./pages/client/ClientDashboard')));
const ClientImportData = lazy(() => u2d(() => import('./pages/client/ClientImportData')));
const ClientInbox = lazy(() => u2d(() => import('./pages/client/ClientInbox')));
const ClientInvoiceReports = lazy(() => u2d(() => import('./pages/client/InvoiceReports')));
const ClientMenuShell = lazy(() => u2d(() => import('./pages/client/ClientMenuShell')));
const ClientUserAdmin = lazy(() => u2d(() => import('./pages/client/UserAdmin')));
const ClientUserEdit = lazy(() => u2d(() => import('./pages/client/UserEdit')));
const CountryReports = lazy(() => u2d(() => import('./pages/client/CountryReports')));
const ElectronicBillingLog = lazy(() => u2d(() => import('./pages/client/ElectronicBillingLog')));
const EmailHistory = lazy(() => u2d(() => import('./pages/client/EmailHistory')));
const EmailTemplates = lazy(() => u2d(() => import('./pages/client/EmailTemplates')));
const EmailVendor = lazy(() => u2d(() => import('./pages/client/EmailVendor')));
const ExpiringReports = lazy(() => u2d(() => import('./pages/client/ExpiringReports')));
const HoldManagement = lazy(() => u2d(() => import('./pages/client/HoldManagement')));
const InvoiceEntry = lazy(() => u2d(() => import('./pages/client/InvoiceEntry')));
const InvoiceManagement = lazy(() => u2d(() => import('./pages/client/InvoiceManagement')));
const InvoiceCart  = lazy(() => u2d(() => import('./pages/client/InvoiceCart')));
const InvoiceStatusReport = lazy(() => u2d(() => import('./pages/client/InvoiceStatusReport')));
const InvoiceSubmitted = lazy(() => u2d(() => import('./pages/client/InvoiceSubmitted')));
const MCAWireInstructions = lazy(() => u2d(() => import('./pages/client/MCAWireInstructions')));
const PayrollReports = lazy(() => u2d(() => import('./pages/client/PayrollReports')));
const PurchaseOrderFilter = lazy(() => u2d(() => import('./pages/client/PurchaseOrderFilter')));
const PurchaseOrderReports = lazy(() => u2d(() => import('./pages/client/PurchaseOrderReports')));
const SpotRates = lazy(() => u2d(() => import('./pages/client/SpotRates')));
const SupportContacts = lazy(() => u2d(() => import('./pages/client/SupportContacts')));
const ThankYou = lazy(() => u2d(() => import('./pages/client/ThankYou')));
const TradePending = lazy(() => u2d(() => import('./pages/client/TradePending')));
const TransactionReports = lazy(() => u2d(() => import('./pages/client/TransactionReports')));
const UnpaidPayments = lazy(() => u2d(() => import('./pages/client/UnpaidPayments')));
const VendorHistory = lazy(() => u2d(() => import('./pages/client/VendorHistory')));
const VendorSubmissions = lazy(() => u2d(() => import('./pages/client/VendorSubmissions')));
const VendorTerms = lazy(() => u2d(() => import('./pages/client/VendorTerms')));
const ViewInvoiceReports = lazy(() => u2d(() => import('./pages/client/ViewInvoiceReports')));
const VIPDashboard = lazy(() => u2d(() => import('./pages/client/VIPDashboard')));
const VIPExceptionAudit = lazy(() => u2d(() => import('./pages/client/VIPExceptionAudit')));
const VIPFTPSettings = lazy(() => u2d(() => import('./pages/client/VIPFTPSettings')));   
const VIPInvitation = lazy(() => u2d(() => import('./pages/client/VIPInvitation')));
const VIPInvoiceTracker = lazy(() => u2d(() => import('./pages/client/VIPInvoiceTracker')));
const VIPProfile = lazy(() => u2d(() => import('./pages/client/VIPProfile')));
const VIPSubmissionHistory = lazy(() => u2d(() => import('./pages/client/VIPSubmissionHistory')));
const VIPUserManual = lazy(() => u2d(() => import('./pages/client/VIPUserManual')));
const WeeklyReport = lazy(() => u2d(() => import('./pages/client/WeeklyReport')));
const WireInstructions = lazy(() => u2d(() => import('./pages/client/WireInstructions')));
const W8Forms = lazy(() => u2d(() => import('./pages/client/W8Forms')));

// OTHER
const VideoDisplay = lazy(() => u2d(() => import('./pages/common/VideoDisplay')));

/* Please keep the application neat by alphabetizing your imports and routes! */

function App() {

	/* Corpay IAS React Website	 
	 * Completed - Q1 2024
	 * Lead Systems Architect: Chris Roman
	 * Web Application Developers: Longteng Huang, Gavin Rector, John Conlon
	 * -
	 * Some said it couldn't be done.
	 * Special shout out to Material UI.
	 * R.I.P. Coldfusion
	 * ---------------------------------------------------- */	

	const api = APIConnector();
	const location = useLocation();
	
	var lastPathName = '';
	useEffect(() => {
		if(location.pathname != '/' && location.pathname != lastPathName) {
			api.post('/web/track', { page: location.pathname });
			lastPathName = location.pathname;
		}
	}, [location.key]);

	return (
	<ThemeProvider theme={CorpayTheme}>
	<Suspense>
    <Routes>
		<Route index element={<Login />} />					
		<Route path="tax-bridge/login" element={<TaxBridgeLoginPage />} />

		<Route path="mfa" element={<MultifactorAuth />} />
		<Route path="register" element={ <Registration /> } />
		<Route path="register/tax" element={ <ForeignTaxRegistration /> } />
		<Route path="vendoralert" element={ <Registration /> } />

		<Route path="registered" element={ <Registered /> } />
		<Route path="login" element={<TokenLogin />} />
		<Route path="sso" element={<SsoLanding />} />

		<Route path="videos/:videoId" element={<VideoDisplay />} />

		{/* CLIENT ROUTES */}
		<Route path="client" element={<ClientMenuShell />} >			
			<Route index element={<ClientDashboard />} />
			<Route path="home" element={<ClientDashboard />} />
			<Route path="activity" element={ <ActivityLogWrapper /> } />
			{/* ProtectedRoute by default prevents ViewOnly users */}
			<Route element={<ProtectedRoute hasPermission={(context) => !context.userInfo.viewOnly}/>}> 
				<Route path="batch/upload" element={ <BatchUpload /> } />
				<Route path="batch/review" element={ <BatchReview /> } />
				<Route path="batch/inquiry" element={ <BatchInquiry /> } />
				<Route path="batch/approval" element={ <BatchApproval /> } />
			</Route>
			<Route path="batch/upload/thank-you" element={ <ThankYou />} />
			<Route path="docs/bank-holidays" element={ <PDFDisplay fileName="BankHolidays.pdf" /> } />
			<Route path="docs/expired-policy" element={ <PDFDisplay fileName="ExpiringInvoicesPolicy.pdf" /> } />
			<Route path="docs/vendor-letter" element={ <PDFDisplay fileName="VendorLetter.pdf" /> } />		
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.clientServices.includes("101129") }/>
			}>
				<Route path="ebilling/history" element={ <ElectronicBillingLog/> } />
			</Route>
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.clientServices.includes("60542") }/>
			}>
				<Route path="email/templates" element={ <EmailTemplates /> }/>
				<Route path="file/upload" element={ <UploadInvoices /> }/>
			</Route>	
			<Route path="inbox" element={ <ClientInbox /> } />
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.clientServices.includes("60542") }/>
			}>
				<Route path="invite/homeowner" element={ <PayeeInvite /> }/>
			</Route>
			<Route element={<ProtectedRoute hasPermission={(context) => context.userInfo.entryTier && !context.userInfo.viewOnly}/>}> 
				<Route path="invoice/entry" element={ <InvoiceEntry isSpot={false} /> } />
			</Route>
			<Route path="invoices/expiring" element={ <ExpiringReports /> } />
			<Route path="invoices/held" element={ <HoldManagement /> } />
			<Route element={<ProtectedRoute hasPermission={(context) => context.userInfo.paymentTier && !context.userInfo.viewOnly}/>}> 
				<Route path="invoices/import" element={ <ClientImportData /> } />				
				<Route path="invoices/payment" element={ <ClientInvoiceReports mode="payment" /> } />
			</Route>
			<Route element={
				<ProtectedRoute hasPermission={(context) => !context.userInfo.clientServices.includes("1894") }/>
			}>
				<Route path="invoices/pending" element={ <TradePending isSpot={false}/> }/>
			</Route>
			<Route path="invoices/status" element={ <InvoiceStatusReport/> } />
			<Route path="invoices/unpaid" element={ <UnpaidPayments /> } />
			<Route element={<ProtectedRoute hasPermission={(context) => context.userInfo.paymentTier && !context.userInfo.viewOnly} />}> 
				<Route path="invoices/cart" element={ <InvoiceCart /> } />
			</Route>
			<Route path="invoices/submitted" element={ <InvoiceSubmitted /> } />
			<Route path="named-accounts" element={ <MCAWireInstructions />} />
			<Route path="reports/aging" element={ <AgingReports /> } />
			<Route path="reports/coming-due" element={ <ExpiringReports params="coming-due"/> } />
			<Route path="reports/country" element={<CountryReports /> } />
			<Route path="reports/expiring" element={ <ExpiringReports /> } />
			<Route path="reports/invoice" element={ <ClientInvoiceReports /> } />
			<Route path="reports/invoice/view" element={ <ViewInvoiceReports /> } />
			<Route path="reports/payroll" element={ <PayrollReports /> } />
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.clientServices.includes("87286") }/>
			}>
				<Route path="reports/purchase-orders" element={ <PurchaseOrderFilter /> } />
				<Route path="reports/purchase-orders/view" element={ <PurchaseOrderReports /> } />
			</Route>
			<Route path="reports/transaction" element={ <TransactionReports /> } />
			<Route path="reports/weekly" element={ <WeeklyReport /> } />
			<Route element={<ProtectedRoute hasPermission={(context) => context.userInfo.spotTier && !context.userInfo.viewOnly}/>}> 
				<Route path="spot/payment" element={ <InvoiceEntry isSpot={true} /> } />
			</Route>
			<Route path="spot/pending" element={ <TradePending isSpot={true} /> } />
			<Route path="spot/rates" element={ <SpotRates /> } />
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.clientServices.includes("60542") }/>
			}>
				<Route path="submission/report" element={ <SpanishSubmissionReport /> }/>
			</Route>
			<Route path="support" element={ <SupportContacts />} />
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.isAdmin }/>
			}> 
				<Route path="users/admin" element={ <ClientUserAdmin /> } />
				<Route path="users/admin/edit" element={ <ClientUserEdit /> } />
			</Route>
			<Route path="users/change-password" element={<ClientChangePassword />} />
			<Route path="vendors/history" element={ <VendorHistory /> } />
			<Route path="vendors/terms" element={ <VendorTerms /> } />
			<Route path="vendors/w8-forms" element={ <W8Forms /> } />
			<Route element={ /* Check permissions by passing a function */
				<ProtectedRoute hasPermission={(context) => parseInt(context.userInfo.vipTier) > 0} />
			}> 
				<Route path="vip/audit-report" element={ <VIPExceptionAudit /> } />
				<Route path="vip/dashboard" element={ <VIPDashboard /> } />
				<Route path="vip/email-history" element={ <EmailHistory /> } />
				<Route path="vip/exceptions" element={ <InvoiceManagement /> } />
				<Route path="vip/invite" element={ <VIPInvitation /> } />
				<Route element={
					<ProtectedRoute hasPermission={(context) => context.userInfo.clientServices.includes("88689") }/>
				}>
					<Route path="vip/invoice-tracker" element={ <VIPInvoiceTracker /> } />
				</Route>
				<Route path="vip/profile" element={ <VIPProfile /> } />
				<Route path="vip/send-email" element={ <EmailVendor /> } />
				<Route path="vip/submissions" element={ <VendorSubmissions /> } />
				<Route path="vip/submission-history" element={ <VIPSubmissionHistory /> } />
			</Route>
			<Route element={
				<ProtectedRoute hasPermission={(context) => ((parseInt(context.userInfo.vipTier) > 0) || (context.userInfo.clientServices.includes("60542")))} />
			}> 
				<Route path="vip/templates" element={ <EmailTemplates /> } />
			</Route>
			<Route element={
				<ProtectedRoute hasPermission={(context) => parseInt(context.userInfo.vipTier) > 0} />
			}> 
				<Route path="vip/user-manual" element={ <VIPUserManual manualFor={"Client"} /> } />
				<Route path="vip/ftp-settings" element={ <VIPFTPSettings /> } />
			</Route>
			<Route element={<ProtectedRoute hasPermission={(context) => context.userInfo.spotTier } />}> 
				<Route path="wires/pending" element={ <TradePending isSpot={true}/> } />
			</Route>
			<Route path="wire-instructions" element={ <WireInstructions /> } />
			<Route path="forbidden" element={<Forbidden />} />
			<Route path="*" element={<NotFound />} />
		</Route>		
		
		{/* VENDOR ROUTES */}
		<Route path="vendor" element={<VendorMenuShell />}>		
			<Route index element={<VendorDashboard />} />
			<Route path="home" element={<VendorDashboard />} />
			<Route path="inbox" element={<VendorMessageCenter />} />
			<Route path="contact-us" element={<Contact />} />
			<Route element={
				<ProtectedRoute hasPermission={(context) => parseInt(context.userInfo.userTier) > 0 }/>
			}> 
				<Route path="invoice/entry" element={<VendorInvoiceEntry />} />
				<Route path="invoice/pending" element={<VendorPendingInvoicesWrapper />} />
				<Route path="invoice/management" element={<ExceptionManagement />} />
				<Route path="invoice/rejected" element={<VendorRejectedInvoicesWrapper />} />
			</Route>
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.beneServices.includes("60562") }/>
			}> 
				<Route path="invoice/due" element={<InvoicesDue/>}/>
			</Route>
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.isAdmin }/>
			}> 
				<Route path="users/admin" element={<VendorUserAdmin />} />
			</Route>
			<Route path="users/change-password" element={<VendorChangePassword />} />
			<Route path="users/switch-account" element={<SwitchAccount/>} />
			<Route path="vip/invites" element={<ClientTerms />} />
			<Route path="client-terms" element={<ClientTerms />} />
			<Route path="reports/invoices" element={<InvoiceReports />} />
			<Route path="reports/invoice-status" element={<InvoiceStatus />} />
			<Route path="reports/payments-received" element={<PaymentsReceived />} />
			<Route path="reports/payments-received/results" element={<PaymentsReceivedPost />} />
			<Route path="reports/vip-submissions" element={<SubmissionReport />} />
			<Route path="docs/vip-tutorial" element={<Tutorial />} />
			<Route path="docs/vip-user-manual" element={<VIPUserManual manualFor={"Vendor"} />} />
			<Route path="docs/bank-holidays" element={<PDFDisplay fileName="BankHolidays.pdf" />} />
			<Route path="docs/vendor-letter" element={<PDFDisplay fileName="VendorLetter.pdf" />} />
			<Route path="forbidden" element={<Forbidden />} />
			<Route path="*" element={<NotFound />} />
		</Route>
		
		{/* USPTO ROUTES */}
		<Route path="uspto/login" element={<UsptoLogin />} />
		<Route path="uspto/reset-password" element={<UsptoResetPassword />} />
		<Route path="uspto/matter/login" element={<USPTOMatterLogin />} />
		<Route path="uspto" element={<UsptoMenuShell />}>
			<Route index element={<UsptoDashboard />} />
			<Route path="home" element={<UsptoDashboard />} />
			<Route path="admin/change-password" element={<UsptoChangePassword />} />
			<Route path="client-setup" element={<UsptoClientSetup />} />
			<Route path="client-billing" element={<UsptoClientBilling />} />
			<Route path="export-files" element={<UsptoExportFiles />} />
			<Route path="data-grid-setup" element={<UsptoDataGridSetup />} />
			<Route path="transaction-search" element={<TransactionSearch />} />
			<Route path="codes/:type" element={<CodeManagement />} />
			<Route path="client-codes" element={<ClientCodes />} />
			<Route path="user-setup" element={<UsptoUserSetup />} />
			<Route path="demo" element={<Demo />} />
			<Route path="data-review" element={<DataReview />} />
			<Route path="docket" element={<DocketMain />} />
			<Route path="export/:type" element={<UsptoExportFile />} />
			<Route path="reports/:type" element={<Reporting />} />
			<Route path="daily-history" element={<DailyHistory />} />
			<Route path="import" element={<ImportData />} />
			<Route path="matters" element={<MatterMain />} />
			<Route path="matter-conflicts" element={<UsptoMatterConflicts />} />
			<Route path="missing-users" element={<MissingUsers />} />
			<Route path="parked" element={<ParkedCodes />} />
			<Route path="reconcile" element={<Reconciliation />} />
			<Route path="refunds" element={<Refunds/>} />
			<Route path="summary-vs-detail" element={<SummaryReport/>} />
			<Route path="transaction/:transactionId" element={<TransactionReport />} />
			<Route path="valid-maint" element={<ValidMaintenance />} />
			<Route path="forbidden" element={<Forbidden />} />
			<Route path="*" element={<NotFound />} />
		</Route>

		{/* USCIS ROUTES */}
		<Route path="uscis/try" element={<UscisTry />} />
		<Route path="uscis/about" element={<UscisAbout />} />
		<Route path="uscis/signup" element={<UscisSignup />} />
		<Route path="uscis/login" element={<UscisLogin />} />
		<Route path="uscis/reset-password" element={<UscisResetPassword />} />
		<Route path="uscis/g1450/login" element={<UscisG1450Login />} />
		<Route path="uscis" element={<UscisMenuShell />}>
			<Route index element={<UscisDashboard />} />
			<Route path="home" element={<UscisDashboard />} />
			<Route path="admin/change-password" element={<UscisChangePassword />} />
			<Route path="extension" element={<UscisExtension />} />
			<Route path="g1450-form" element={<ToNavigatorForm />} />
			<Route element={<ProtectedRoute hasPermission={usesNavigatorForm} />}>
				<Route path="navigator-form" element={<NavigatorForm />} />
			</Route>
			<Route element={<ProtectedRoute hasPermission={usesG1450s} />}>
				<Route path="g1450-review" element={<G1450Review />} />
				<Route path="g1450-review-admin" element={<G1450ReviewAdmin />} />
				<Route path="unprocessed" element={<UscisUnProcessed />} />
				<Route path="unprocessed-admin" element={<UscisUnProcessedAdmin />} />
				<Route path="card-event" element={<UscisCardEvent />} />
				<Route path="expired-cards" element={<UscisExpiredCards />} />
				<Route path="form-fee-codes" element={<UscisFormFeeCodes />} />
			</Route>
			<Route path="form-history" element={<UscisDashboard />} />
			<Route path="downloads" element={<UscisDashboard />} />
			<Route path="daily-history" element={<UscisDailyHistory />} />
			<Route path="reconcile" element={<UscisReconciliation />} />
			<Route element={<ProtectedRoute hasPermission={usesG1450s} />}>
				<Route path="import" element={<UscisImportData />} />
			</Route>
			<Route path="import-h1b" element={<UscisImportH1B />} />
			<Route path="h1b-review" element={<UscisH1BCapReview />} />
			<Route path="templates" element={<UscisDashboard />} />
			<Route path="review" element={<UscisReview />} />
			<Route path="review-admin" element={<CardReviewAdmin />} />
			<Route path="data-issues-admin" element={<DataIssuesAdmin />} />
			<Route path="card-decline" element={<UscisCardDecline />} />
			<Route path="card-refund" element={<UscisCardRefund />} />
			<Route path="event-history" element={<UscisEventHistory />} />
			<Route path="partial-payments" element={<UscisPartialPayments />} />
			<Route path="valid-maintenance" element={<UscisValidMaintenance />} />
			<Route path="client-setup" element={<UscisClientSetup />} />
			<Route path="client-billing" element={<UscisClientBilling />} />
			<Route path="user-setup" element={<UscisUserSetup />} />
			<Route path="client-field-setup" element={<UscisClientFieldSetup />} />
			<Route path="extension-field-setup" element={<UscisExtensionFieldSetup />} />
			<Route path="card-control-setup" element={<UscisCardControlSetup />} />
			<Route path="extension-site-accounts" element={<UscisExtensionSiteAccountSetup />} />
			<Route path="vendor-mapping" element={<UscisVendorMapping />} />
			<Route path="export-files" element={<UscisExportFiles />} />
			<Route path="client-import-setup" element={<UscisClientImportSetup />} />
			<Route element={<ProtectedRoute hasPermission={usesChromeRiverImport} />}>
				<Route path="chrome-river-import" element={<UscisChromeRiverImport />} />
			</Route>
			<Route path="card-verification" element={<UscisCardVerification />} />
			<Route path="sftp-imports" element={<UscisSftpImports />} />
			<Route path="client-data-grid-setup" element={<UscisClientDataGridSetup />} />
			<Route path="demo" element={<UscisDemo />} />
			<Route element={
				<ProtectedRoute hasPermission={(context) => context.userInfo.permissions && context.userInfo.permissions.includes("1067") }/>
			}>
				<Route path="balance" element={<UscisBalanceCalculation />} />
			</Route>
			<Route path="forbidden" element={<Forbidden />} />
			<Route path="*" element={<NotFound />} />
		</Route>

		{/* APPROVAL ROUTES */}
		<Route path="approval/login" element={<ApprovalLogin />} />
		<Route path="approval/dashboard" element={<ApprovalDashboard />} />
		
		<Route path="*" element={<Navigate to="/" replace />} />			
	</Routes>
	</Suspense>
	</ThemeProvider>
  );
}

export default App;
